import { Box, Button, Grid, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import StyledTextField from '../../components/Common/StyledTextField';
import CustomPhoneNumberInput from '../../components/CustomPhoneNumberInput/CustomPhoneNumberInput';
import { StyledCheckbox } from '../../components/Bookings/SetAvailability/SetAvailabilityStep';
import { sanitizeInputName } from '../../utils/userInputUtils';
import { useAppSelector } from '../../redux/hooks';
import { fetchCountryCode } from '../../redux/booking/bookingSlice';
import {
  initialValues,
  FormValues,
  getValidationSchema,
} from '../../utils/validations/createCoachMiniwebBookingForm';
import {
  createCoachMiniwebBookingRequest,
  ICoachMiniWebBooking,
} from '../../redux/miniweb/miniwebSlice';
import { COACH_BOOKING_CONFIRMATION, COACH_PROFILE_ROUTE } from '../../utils/routes';
import { toastStyles, truncateFileName } from '../../utils/utils';
import LoadingButton from '../../components/Common/LoadingButton';
import MiniWebSiteHeader from './MiniWebSiteHeader/MiniWebSiteHeader';
import MiniWebSiteFooter from './MiniWebSiteFooter/MiniWebSiteFooter';

const MiniWebSiteSetBookingDetails = () => {
  const { t, i18n } = useTranslation();
  const countryCode = useAppSelector((state) => state.booking.countryCode);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { username } = useParams<{ username: string }>();
  const { servicePackage, startAt, duration } = location.state || {};
  const { createMiniwebBookingLoading, coachMiniWebBookingSuccess } = useAppSelector(
    (state) => state.miniweb
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const isArabic = i18n.language === 'ar';

  useEffect(() => {
    if (!countryCode) {
      dispatch(fetchCountryCode());
    }
  }, [countryCode]);

  useEffect(() => {
    if (username && (!servicePackage || !startAt || !duration)) {
      navigate(COACH_PROFILE_ROUTE.replace(':username', username));
    }
  }, [navigate, servicePackage, startAt, duration]);

  const handleSaveBookingDetails = (values: FormValues) => {
    const bookingDetails: ICoachMiniWebBooking = {
      username: username ?? '',
      clientFirstName: values.firstName,
      clientLastName: values.lastName,
      clientEmail: values.email,
      clientPhone: values.phoneNumber,
      date: startAt,
      duration,
      servicePackageId: servicePackage.id,
      isAgreedToTerms: values.isAgreedToPrivacyPolicy,
      isSubscribedToNewsletter: values.isSubscribedToNewsletter,
      referenceNote: values.references,
    };

    const referenceFile = uploadedFile ? [uploadedFile] : null;

    const coachMiniWebBookingInput = {
      coachMiniWebBooking: bookingDetails,
      referenceFile,
    };
    dispatch(createCoachMiniwebBookingRequest(coachMiniWebBookingInput));
  };

  const formik = useFormik({
    initialValues,
    validationSchema: getValidationSchema(t),
    onSubmit: handleSaveBookingDetails,
  });

  useEffect(() => {
    formik.validateForm();
  }, [i18n.language]);

  const handleEmailChange = (field: string, value: string) => {
    formik.handleChange(field)(value.toLowerCase());
  };

  const handleNameChange = (field: string, value: string) => {
    formik.handleChange(field)(sanitizeInputName(value));
  };

  const handlePhoneNumberChange = (pNumber: string) => {
    formik.setFieldValue('phoneNumber', pNumber);
  };

  const handleNavigateBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (!createMiniwebBookingLoading && coachMiniWebBookingSuccess && username) {
      navigate(COACH_BOOKING_CONFIRMATION.replace(':username', username));
    }
  }, [createMiniwebBookingLoading, coachMiniWebBookingSuccess]);

  const onDrop = (acceptedFiles: File[]) => {
    // restrict to accept only 1 file
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.size > 16 * 1024 * 1024) {
        // Check file size, 16MB limit
        toast.error('File size exceeds 16MB.', toastStyles.error);
        return;
      }
      setUploadedFile(file);
      formik.setFieldValue('references', file.name);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
      'image/jpeg': [],
      'image/png': [],
      'image/heic': [],
      'text/plain': [],
      'application/vnd.ms-word.document.macroEnabled.12': [],
    },
  });

  return (
    <Grid
      container
      display="flex"
      sx={{
        background: 'linear-gradient(163deg, #FFF 10.13%, #FFF4F0 89.18%)',
      }}
      minHeight="100vh"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <MiniWebSiteHeader />
      <Grid
        item
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
        mt={5}
        mb={5}
        sx={{ flexGrow: 1, gap: '18px' }}
      >
        <Box
          sx={{
            width: '361px',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0px 4px 40px 0px #FFDDC840',
            borderRadius: '10px',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '99px',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              borderBottom: '1px solid #EEEEEE',
              padding: '0 16px',
            }}
          >
            <Button
              onClick={handleNavigateBack}
              sx={{
                width: '10px',
                height: '24px',
                background: 'transparent !important',
              }}
            >
              <img src="/assets/icons/back-arrow.svg" alt="back-arrow" height={24} width={24} />
            </Button>
            {servicePackage && startAt && duration && (
              <Box
                sx={{
                  width: '285.67px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '12px',
                  marginRight: isArabic ? '0px' : '63px',
                  marginLeft: isArabic ? '63px' : '0px',
                }}
              >
                <Tooltip
                  title={servicePackage.title}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: '#2A3C67',
                        color: 'white',
                      },
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    color="#2A3C67"
                    maxWidth="200px"
                    sx={{
                      cursor: 'pointer',
                      flexGrow: 1,
                      marginLeft: '8px',
                      marginRight: '8px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textAlign: 'center',
                    }}
                  >
                    {servicePackage.title}
                  </Typography>
                </Tooltip>
                <Typography
                  variant="bodyBold"
                  color="#2A3C67"
                  sx={{
                    unicodeBidi: 'plaintext',
                  }}
                >
                  {dayjs(startAt).format('hh:mm A')} | {dayjs(startAt).format('DD MMM YYYY')}
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              gap: '4px',
              borderRadius: '0px 0px 10px 10px',
              padding: '20px 16px 0 16px',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="meta" fontSize={13} color="#2A3C67">
                {t('miniWebSite.booking.firstName')}
              </Typography>
              <StyledTextField
                bordercolor="#7CAEBE"
                name="firstName"
                placeholder={t('miniWebSite.booking.firstName')}
                inputPadding="12px 14px"
                value={formik.values.firstName}
                onChange={(event) => handleNameChange('firstName', event.target.value)}
                onBlur={formik.handleBlur}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                required
              />
            </Box>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="meta" fontSize={13} color="#2A3C67">
                {t('miniWebSite.booking.lastName')}
              </Typography>
              <StyledTextField
                fullWidth
                bordercolor="#7CAEBE"
                name="lastName"
                placeholder={t('miniWebSite.booking.lastName')}
                inputPadding="12px 14px"
                value={formik.values.lastName}
                onChange={(event) => handleNameChange('lastName', event.target.value)}
                onBlur={formik.handleBlur}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
                required
              />
            </Box>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="meta" fontSize={13} color="#2A3C67">
                {t('miniWebSite.booking.email')}
              </Typography>
              <StyledTextField
                bordercolor="#7CAEBE"
                name="email"
                placeholder={t('miniWebSite.booking.email')}
                inputPadding="12px 14px"
                value={formik.values.email}
                onChange={(event) => handleEmailChange('email', event.target.value)}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                required
              />
            </Box>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="meta" fontSize={13} color="#2A3C67">
                {t('miniWebSite.booking.phoneNumber')}
              </Typography>
              <CustomPhoneNumberInput
                name=""
                isMiniWeb
                value={formik.values.phoneNumber}
                handleChange={handlePhoneNumberChange}
                onBlur={formik.handleBlur}
                error={formik.errors.phoneNumber}
                countryCode={countryCode}
              />
            </Box>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="meta" fontSize={13} color="#2A3C67">
                {t('miniWebSite.booking.references')}
              </Typography>
              <StyledTextField
                bordercolor="#7CAEBE"
                name="references"
                placeholder={t('miniWebSite.booking.referenceNote')}
                inputPadding="12px 14px"
                value={formik.values.references}
                onBlur={formik.handleBlur}
                error={formik.touched.references && Boolean(formik.errors.references)}
                helperText={formik.touched.references && formik.errors.references}
                InputProps={{
                  readOnly: true,
                }}
              />
              {!uploadedFile ? (
                <Box
                  {...getRootProps()}
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: '2px dashed #7CAEBE80',
                    borderRadius: '10px',
                    padding: '16px',
                    marginTop: '8px',
                    cursor: 'pointer',
                  }}
                >
                  <input {...getInputProps()} />
                  <img
                    src="/assets/icons/cloud-upload.svg"
                    alt="upload-icon"
                    width={24}
                    height={24}
                  />
                  <Typography variant="body2" color="#7CAEBE">
                    {t('miniWebSite.booking.uploadDocument')}
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '8px',
                    marginLeft: '16px',
                    gap: '4px',
                  }}
                >
                  <img src="/assets/icons/booking-reference.svg" alt="copy" />
                  <Typography
                    variant="body2"
                    color="#2A3C67"
                    maxWidth="200px"
                    sx={{
                      flexGrow: 1,
                      marginLeft: '8px',
                      marginRight: '8px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      textAlign: 'flex-start',
                    }}
                  >
                    {truncateFileName(uploadedFile.name, 25)}
                  </Typography>
                  <Button
                    onClick={() => {
                      setUploadedFile(null);
                      formik.setFieldValue('references', '');
                    }}
                    sx={{
                      marginLeft: 'auto',
                      padding: '0',
                      minWidth: 'auto',
                      color: '#2A3C67',
                    }}
                  >
                    <img
                      src="/assets/icons/trash-bin-red.svg"
                      alt="delete-icon"
                      width={24}
                      height={24}
                    />
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              paddingLeft: isArabic ? '0px' : '16px',
              paddingRight: isArabic ? '16px' : '0px',
              paddingTop: '16px',
              paddingBottom: '16px',
            }}
          >
            <Grid display="flex" flexDirection="row" alignItems="center" marginBottom="-10px">
              <StyledCheckbox
                onClick={() =>
                  formik.setFieldValue(
                    'isAgreedToPrivacyPolicy',
                    !formik.values.isAgreedToPrivacyPolicy
                  )
                }
                checked={formik.values.isAgreedToPrivacyPolicy}
                inputProps={{ 'aria-label': 'controlled' }}
                checkedIcon={
                  <Box
                    component="img"
                    src="/assets/icons/check-box-checked.svg"
                    width={20}
                    height={20}
                  />
                }
                icon={
                  <Box component="img" src="/assets/icons/check-box.svg" width={20} height={20} />
                }
              />
              <Typography variant="meta" color="primary">
                {t('miniWebSite.booking.privacyPolicyAgreement')}
              </Typography>
            </Grid>
            <Grid display="flex" flexDirection="row" alignItems="center">
              <StyledCheckbox
                onClick={() =>
                  formik.setFieldValue(
                    'isSubscribedToNewsletter',
                    !formik.values.isSubscribedToNewsletter
                  )
                }
                checked={formik.values.isSubscribedToNewsletter}
                inputProps={{ 'aria-label': 'controlled' }}
                checkedIcon={
                  <Box
                    component="img"
                    src="/assets/icons/check-box-checked.svg"
                    width={20}
                    height={20}
                  />
                }
                icon={
                  <Box component="img" src="/assets/icons/check-box.svg" width={20} height={20} />
                }
              />
              <Typography variant="meta" color="primary">
                {t('miniWebSite.booking.subscribeToNewsletter')}
              </Typography>
            </Grid>
          </Box>
          <Box
            sx={{
              width: '100%',
              padding: '0 16px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: '12px',
            }}
          >
            <form onSubmit={formik.handleSubmit}>
              {!createMiniwebBookingLoading ? (
                <Button
                  variant="contained"
                  sx={{
                    background:
                      'var(--Brand-Cadet-Blue, linear-gradient(138deg, #6599A5 24.11%, #3B667E 83.2%))',
                    borderRadius: '15px',
                    '&:hover': {
                      background:
                        'var(--Brand-Cadet-Blue, linear-gradient(138deg, #4e7c91 24.11%, #2d4f5f 83.2%))',
                    },
                  }}
                  fullWidth
                  type="submit"
                  disabled={!formik.isValid || createMiniwebBookingLoading}
                >
                  {t('miniWebSite.booking.confirm')}
                </Button>
              ) : (
                <LoadingButton paddingY={12} paddingX={62}>
                  {t('bookings.coachServicePackages.confirm')}
                </LoadingButton>
              )}
            </form>
          </Box>
        </Box>
      </Grid>
      <MiniWebSiteFooter />
    </Grid>
  );
};

export default MiniWebSiteSetBookingDetails;
